import api from "../../../services/api";
export type InvoiceStatus = "PENDING" | "INCLUDED" | "LINKED" | "OMITTED";
export interface CostInvoice {
  id: string;
  source: "KSEF" | "MANUAL";
  ksefNumber: string | null;
  invoiceNumber: string;
  sellerName: string;
  sellerTaxId: string;
  issueDate: string;
  currency: string;
  netAmount: number;
  invoiceType: string;
  status: InvoiceStatus;
  version: number;
  hasFile: boolean;
  companyCostId: string | null;
  correctedInvoiceId: string | null;
  decidedAt: string | null;
  decision: {
    month?: string;
    category?: string;
    reason?: string;
    targetLabel?: string;
  } | null;
}
export interface InvoiceOverview {
  invoices: CostInvoice[];
  hasMore: boolean;
  canManage: boolean;
  ksefReady: boolean;
  environment: string;
  lastSync: { completedAt: string } | null;
}
function normalize(row: CostInvoice): CostInvoice {
  return {
    ...row,
    netAmount: Number(row.netAmount),
    version: Number(row.version),
    hasFile: row.hasFile === true,
  };
}
export async function fetchCostInvoices(
  status: string,
  offset: number,
): Promise<InvoiceOverview> {
  const { data } = await api.get("/company-cost-invoices", {
    params: { status: status || undefined, offset },
  });
  return { ...data, invoices: data.invoices.map(normalize) };
}
export async function createCostInvoice(
  values: Record<string, unknown>,
  file?: File,
) {
  const form = new FormData();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== undefined && value !== null) form.append(key, String(value));
  });
  if (file) form.append("file", file);
  return normalize(
    (
      await api.post("/company-cost-invoices", form, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    ).data,
  );
}
export async function updateCostInvoice(
  id: string,
  values: Record<string, unknown>,
) {
  return normalize(
    (await api.put(`/company-cost-invoices/${encodeURIComponent(id)}`, values))
      .data,
  );
}
export async function decideCostInvoice(
  id: string,
  values: Record<string, unknown>,
) {
  return normalize(
    (
      await api.post(
        `/company-cost-invoices/${encodeURIComponent(id)}/decision`,
        values,
      )
    ).data,
  );
}
export async function fetchInvoiceTargets(
  type: string,
  search: string,
): Promise<Array<{ id: string; label: string }>> {
  return (
    await api.get("/company-cost-invoices/targets", {
      params: { type, search },
    })
  ).data;
}
export async function syncCostInvoices(values: {
  from?: string;
  to?: string;
  runId?: string;
}): Promise<{
  runId: string;
  done: boolean;
  imported: number;
  duplicates: number;
}> {
  return (
    await api.post("/company-cost-invoices/sync", values, { timeout: 120000 })
  ).data;
}
export async function downloadCostInvoice(invoice: CostInvoice) {
  const { data, headers } = await api.get(
    `/company-cost-invoices/${encodeURIComponent(invoice.id)}/file`,
    { responseType: "blob", timeout: 120000 },
  );
  const blob = new Blob([data], { type: headers["content-type"] });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const mime = String(headers["content-type"]);
  link.download = `${invoice.invoiceNumber.replace(/[^a-zA-Z0-9._-]/g, "_")}.${mime.includes("xml") ? "xml" : mime.includes("pdf") ? "pdf" : mime.includes("png") ? "png" : "jpg"}`;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function costInvoiceError(error: unknown, fallback: string): string {
  const candidate = error as { response?: { data?: { message?: unknown } } };
  return typeof candidate.response?.data?.message === "string"
    ? candidate.response.data.message
    : fallback;
}
